import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import OverflowMenu from '@/shared/components/ui/OverflowMenu'

interface DataCardProps {
  icon: ReactNode
  title: string
  subtitle?: string
  iconWrapperClass?: string
  badge?: {
    label: React.ReactNode
    variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
    icon?: React.ReactNode
  }
  media?: ReactNode
  fields?: Array<{
    label: string
    value: string | ReactNode
  }>
  actions?: Array<{
    label: string
    icon?: ReactNode
    onClick: () => void
    variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
    hidden?: boolean
  }>
  actionsNode?: ReactNode
  footer?: ReactNode
  onClick?: () => void
  
  // Stamp overlay (e.g. "PAGO")
  stamp?: string

  // Bulk selection props (optional)
  selectable?: boolean
  isSelected?: boolean
  onToggleSelection?: () => void
}

export function DataCard({
  icon,
  iconWrapperClass,
  title,
  subtitle,
  badge,
  media,
  fields = [],
  actions = [],
  actionsNode,
  footer,
  onClick,
  stamp,
  selectable = false,
  isSelected = false,
  onToggleSelection
}: DataCardProps) {
  const { t } = useTranslation()
  const [isMobile, setIsMobile] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [longPressActive, setLongPressActive] = useState(false)
  const [badgeAnimating, setBadgeAnimating] = useState(false)
  const longPressTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const badgeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => {
      window.removeEventListener('resize', checkMobile)
      if (longPressTimer.current) clearTimeout(longPressTimer.current)
      if (badgeTimer.current) clearTimeout(badgeTimer.current)
    }
  }, [])
  const badgeColors = {
    primary: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
    success: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
    warning: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
    danger: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    info: 'bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300'
  }

  const actionColors = {
    primary: 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20',
    danger: 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
  }

  const visibleActions = actions.filter(action => !action.hidden)

  const handleCardClick = () => {
    if (!onClick) return
    onClick()
  }

  // Long-press handler for mobile: show actions on ~500ms hold
  const handlePointerDown = () => {
    if (!isMobile || !visibleActions.length) return
    longPressTimer.current = setTimeout(() => {
      setLongPressActive(true)
    }, 500)
  }

  const handlePointerUp = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current)
  }
  const handlePointerCancel = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current)
  }

  return (
    <div 
      aria-selected={isSelected}
      className={`group relative bg-white dark:bg-slate-800 rounded-lg border ${
        isSelected 
          ? 'border-emerald-500 dark:border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' 
          : 'border-slate-200 dark:border-slate-700 hover:border-emerald-500 dark:hover:border-emerald-500'
      } ${media ? 'overflow-hidden' : 'p-4'} hover:shadow-lg transition-all ${
        onClick ? 'cursor-pointer' : ''
      }`}
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false)
        setLongPressActive(false) // Reset long-press on mouse leave
      }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      {/* Actions header bar (when actionsNode exists, render it in a dedicated section at top) */}
      {actionsNode && (
        <div className="absolute top-3 right-3">
          {actionsNode}
        </div>
      )}

      {/* Overflow menu: reúne seleção e ações em um menu compacto (only when no actionsNode) */}
      {!actionsNode && (selectable || visibleActions.length > 0) && (
        <div className="absolute top-4 right-4">
          <OverflowMenu
            items={[
              ...(selectable ? [{
                label: t('list.selectItemGeneric') || 'Marcar',
                checkable: true,
                checked: isSelected,
                onToggle: () => onToggleSelection?.()
              }] : []),
              ...visibleActions.map(action => ({
                label: action.label,
                icon: action.icon,
                onClick: action.onClick,
                danger: action.variant === 'danger'
              }))
            ]}
          />
        </div>
      )}

      {/* Selected badge (top-left) - clickable to unselect */}
      {isSelected && (
        <button
          type="button"
          aria-pressed="true"
          aria-label={t('list.unselectItem') || 'Desmarcar'}
          onClick={(e) => {
            e.stopPropagation()
            if (badgeAnimating) return
            setBadgeAnimating(true)
            // play a short animation before toggling selection so user sees feedback
            badgeTimer.current = setTimeout(() => {
              onToggleSelection?.()
              setBadgeAnimating(false)
            }, 160)
          }}
          className={`absolute -top-2 left-3 w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-lg border-2 border-white dark:border-slate-800 transition transform duration-150 ${badgeAnimating ? 'scale-90 opacity-60' : 'scale-100 opacity-100'}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" className="w-4 h-4" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 12l4 4L19 6" />
          </svg>
        </button>
      )}

      {media && (
        <div className="relative aspect-[4/3] overflow-hidden bg-slate-100 dark:bg-slate-900">
          {media}
        </div>
      )}
      
      <div className={`${media ? 'p-4' : ''} flex items-start justify-between mb-4`}>
        <div className={`flex-1 ${(actionsNode || selectable || visibleActions.length > 0) ? 'pr-8' : ''}`}>
              <div className="flex items-start gap-1 mb-1">
              <div className={iconWrapperClass ?? "p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex-shrink-0"}>
                {icon}
              </div>
            <div className="flex-1 min-w-0">
              <h3 className={`text-base font-semibold text-slate-900 dark:text-white truncate`}>
                {title}
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                {subtitle && !isMobile && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                    {subtitle}
                  </p>
                )}
                {badge && (
                  <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full mt-1 ${badgeColors[badge.variant || 'primary']}`}>
                    {badge.icon}
                    {badge.label}
                  </span>
                )}
              </div>
            </div>
          </div>

          {fields.length > 0 && (
            <div className="space-y-1 mt-3">
              {fields.map((field, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500 dark:text-slate-400">{field.label}:</span>
                  <span className="font-medium text-slate-700 dark:text-slate-300">{field.value}</span>
                </div>
              ))}
            </div>
          )}

          {footer && (
            <div className="mt-3">
              {footer}
            </div>
          )}
        </div>
      </div>

      {/* A UI de ações inline foi substituída pelo OverflowMenu acima */}

      {/* Diagonal stamp watermark (card-safe) */}
      {stamp && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg" style={{ zIndex: 0 }}>
          <div className="absolute inset-0 flex items-center justify-center" style={{ transform: 'rotate(-32deg)' }}>
            <span
              aria-hidden
              className="block font-black uppercase select-none text-emerald-600 dark:text-emerald-400"
              style={{
                width: '200%',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                fontSize: 'clamp(1.5rem, 6vw, 2.2rem)',
                letterSpacing: '0.4em',
                opacity: 0.2,
                WebkitTextStroke: '1px currentColor',
                paintOrder: 'stroke fill',
              }}
            >
              {stamp}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

// (cleanup handled in the effect above)
